import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Chip,
  TextField,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Divider,
  Stack,
  IconButton,
  Tooltip,
  Alert,
  Snackbar,
  Avatar,
  Container,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme
} from "@mui/material";
import {
  Receipt,
  Payment,
  QrCode,
  History,
  CheckCircle,
  Pending,
  Error,
  Info,
  Download,
  CreditCard,
  CalendarToday,
  AccountBalance,
  ContentCopy,
  CheckCircleOutline,
  GetApp,
  PictureAsPdf,
  Print,
  AttachMoney,
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import QRCode from "react-qr-code";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import {
  getSubscriptionDetails,
  getCurrentInvoice,
  getMyInvoices,
  getSystemSetting,
  submitPayment,
} from "../../redux/slices/billingSlice";

const Billing = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const qrRef = useRef();

  const [utrNumber, setUtrNumber] = useState("");
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });
  const [copied, setCopied] = useState(false);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);

  const {
    subscription,
    currentInvoice,
    invoices,
    setting,
    loading,
  } = useSelector((state) => state.billing);

  useEffect(() => {
    dispatch(getSubscriptionDetails());
    dispatch(getCurrentInvoice());
    dispatch(getMyInvoices());
    dispatch(getSystemSetting());
  }, [dispatch]);

  // Status Chip Component
  const StatusChip = ({ status }) => {
    const configs = {
      ACTIVE: { color: "success", icon: <CheckCircle />, label: "Active" },
      PAID: { color: "success", icon: <CheckCircle />, label: "Paid" },
      PENDING: { color: "warning", icon: <Pending />, label: "Pending" },
      PAYMENT_SUBMITTED: { color: "info", icon: <Info />, label: "Under Review" },
      EXPIRED: { color: "error", icon: <Error />, label: "Expired" },
      REJECTED: { color: "error", icon: <Error />, label: "Rejected" },
    };
    const config = configs[status] || { color: "default", icon: null, label: status || "N/A" };
    
    return (
      <Chip
        icon={config.icon}
        label={config.label}
        color={config.color}
        size="small"
        sx={{ fontWeight: 600, borderRadius: "8px" }}
      />
    );
  };

  // Generate UPI link
  const generateUPILink = () => {
    if (!setting?.upiId) return null;
    const amount = currentInvoice?.amount || 0;
    const name = setting?.merchantName || "RestaurantWala";
    return `upi://pay?pa=${setting.upiId}&pn=${encodeURIComponent(name)}&am=${amount}&cu=INR`;
  };

  // Handle payment submit
  const handleSubmitPayment = () => {
    if (!utrNumber.trim()) {
      setSnackbar({ open: true, message: "Please enter UTR Number", severity: "warning" });
      return;
    }
    if (!currentInvoice?._id) {
      setSnackbar({ open: true, message: "No pending invoice found", severity: "error" });
      return;
    }

    dispatch(submitPayment({ invoiceId: currentInvoice._id, utrNumber }))
      .then((result) => {
        if (result.meta.requestStatus === "fulfilled") {
          setSnackbar({ open: true, message: "Payment submitted successfully!", severity: "success" });
          setUtrNumber("");
          dispatch(getCurrentInvoice());
          dispatch(getMyInvoices());
        } else {
          setSnackbar({ open: true, message: "Failed to submit payment. Please try again.", severity: "error" });
        }
      });
  };

  // Copy UPI ID
  const handleCopyUPI = () => {
    if (setting?.upiId) {
      navigator.clipboard.writeText(setting.upiId);
      setCopied(true);
      setSnackbar({ open: true, message: "UPI ID copied!", severity: "success" });
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // Download QR Code
  const handleDownloadQR = () => {
    if (qrRef.current) {
      html2canvas(qrRef.current, { scale: 2, backgroundColor: '#ffffff' })
        .then((canvas) => {
          const link = document.createElement('a');
          link.download = 'upi-qr-code.png';
          link.href = canvas.toDataURL('image/png');
          link.click();
          setSnackbar({ open: true, message: "QR Code downloaded!", severity: "success" });
        });
    }
  };

  // Download Invoice PDF
  const handleDownloadInvoice = (invoice) => {
    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      
      doc.setFontSize(20);
      doc.setTextColor(255, 107, 0);
      doc.text("INVOICE", pageWidth / 2, 20, { align: "center" });
      
      doc.setFontSize(12);
      doc.setTextColor(100);
      doc.text(`Invoice #${invoice.invoiceNumber}`, pageWidth / 2, 30, { align: "center" });
      
      doc.line(20, 35, pageWidth - 20, 35);
      
      doc.setFontSize(10);
      doc.setTextColor(60);
      let yPos = 50;
      
      const details = [
        ["Invoice Number", `#${invoice.invoiceNumber}`],
        ["Amount", `₹${invoice.amount?.toLocaleString()}`],
        ["Status", invoice.status],
        ["Due Date", new Date(invoice.dueDate).toLocaleDateString("en-IN")],
        ["Created Date", new Date(invoice.createdAt).toLocaleDateString("en-IN")],
      ];
      
      details.forEach(([label, value]) => {
        doc.text(label, 20, yPos);
        doc.text(value, pageWidth - 20, yPos, { align: "right" });
        yPos += 8;
      });
      
      doc.line(20, yPos + 5, pageWidth - 20, yPos + 5);
      
      doc.setFontSize(8);
      doc.setTextColor(150);
      doc.text("Thank you for your business!", pageWidth / 2, yPos + 20, { align: "center" });
      doc.text(`Generated on: ${new Date().toLocaleString()}`, pageWidth / 2, yPos + 25, { align: "center" });
      
      doc.save(`Invoice_${invoice.invoiceNumber}.pdf`);
      setSnackbar({ open: true, message: "Invoice downloaded!", severity: "success" });
    } catch (error) {
      setSnackbar({ open: true, message: "Failed to download invoice", severity: "error" });
    }
  };

  // Get days remaining
  const getDaysRemaining = (expiryDate) => {
    if (!expiryDate) return null;
    const diff = new Date(expiryDate) - new Date();
    return Math.ceil(diff / (1000 * 60 * 60 * 24));
  };

  const daysRemaining = getDaysRemaining(subscription?.planExpiryDate);

  // Loading State
  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <Skeleton variant="rectangular" height={100} sx={{ borderRadius: 2 }} />
          <Grid container spacing={3}>
            {[1, 2, 3, 4].map((i) => (
              <Grid item size={{ xs: 12, sm: 6, md: 3 }} key={i}>
                <Skeleton variant="rectangular" height={100} sx={{ borderRadius: 2 }} />
              </Grid>
            ))}
          </Grid>
          <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 2 }} />
          <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 2 }} />
        </Box>
      </Container>
    );
  }

  return (
    <Box  sx={{ mb: 4 }}>
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 3,
          mb: 4,
          bgcolor: "var(--bg-light)",
          border: "1px solid var(--border)",
        }}
      >
        <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h4" fontWeight={800} color="var(--text)">
              Billing Dashboard
            </Typography>
            <Typography variant="body2" color="var(--text-light)">
              Manage your subscription and payment history
            </Typography>
          </Box>
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              startIcon={<History />}
              sx={{
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                borderColor: "var(--border)",
                color: "var(--text)",
              }}
            >
              History
            </Button> 
            <Button
              variant="contained"
              startIcon={<QrCode />}
              onClick={() => setQrDialogOpen(true)}
              sx={{
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                bgcolor: "var(--primary)",
                "&:hover": { bgcolor: "var(--primary-dark)" },
              }}
            >
              Scan & Pay
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {/* Stats Cards - Fixed Grid Syntax */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ borderRadius: 3, border: "1px solid var(--border)" }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar sx={{ bgcolor: "var(--bg-primary-soft)", color: "var(--primary)" }}>
                  <Receipt />
                </Avatar>
                <Box>
                  <Typography variant="body2" color="var(--text-light)">Status</Typography>
                  <StatusChip status={subscription?.subscriptionStatus} />
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ borderRadius: 3, border: "1px solid var(--border)" }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar sx={{ bgcolor: "var(--bg-primary-soft)", color: "var(--primary)" }}>
                  <CalendarToday />
                </Avatar>
                <Box>
                  <Typography variant="body2" color="var(--text-light)">Days Remaining</Typography>
                  <Typography variant="h6" fontWeight={700}>
                    {daysRemaining > 0 ? `${daysRemaining}d` : "Expired"}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ borderRadius: 3, border: "1px solid var(--border)" }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar sx={{ bgcolor: "var(--bg-primary-soft)", color: "var(--primary)" }}>
                  <AttachMoney />
                </Avatar>
                <Box>
                  <Typography variant="body2" color="var(--text-light)">Current Invoice</Typography>
                  <Typography variant="h6" fontWeight={700}>
                    {currentInvoice ? `₹${currentInvoice.amount?.toLocaleString()}` : "No dues"}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ borderRadius: 3, border: "1px solid var(--border)" }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar sx={{ bgcolor: "var(--bg-primary-soft)", color: "var(--primary)" }}>
                  <CreditCard />
                </Avatar>
                <Box>
                  <Typography variant="body2" color="var(--text-light)">Total Invoices</Typography>
                  <Typography variant="h6" fontWeight={700}>
                    {invoices?.length || 0}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={4}>
        {/* Subscription Details */}
        <Grid item size={{ xs: 12, md: 6 }}>
          <Card sx={{ borderRadius: 3, border: "1px solid var(--border)", height: "100%" }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={700} sx={{ mb: 3 }}>
                Subscription Details
              </Typography>

              <Box sx={{ bgcolor: "var(--bg-light)", p: 3, borderRadius: 2, mb: 3 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" color="var(--text-light)">Status</Typography>
                  <StatusChip status={subscription?.subscriptionStatus} />
                </Stack>
                <Divider sx={{ my: 2 }} />
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="var(--text-light)">Start Date</Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {subscription?.planStartDate
                      ? new Date(subscription.planStartDate).toLocaleDateString("en-IN")
                      : "-"}
                  </Typography>
                </Stack>
                <Divider sx={{ my: 2 }} />
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="var(--text-light)">Expiry Date</Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {subscription?.planExpiryDate
                      ? new Date(subscription.planExpiryDate).toLocaleDateString("en-IN")
                      : "-"}
                  </Typography>
                </Stack>
                <Divider sx={{ my: 2 }} />
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="var(--text-light)">Grace Period</Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {subscription?.graceExpiryDate
                      ? new Date(subscription.graceExpiryDate).toLocaleDateString("en-IN")
                      : "-"}
                  </Typography>
                </Stack>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Current Invoice */}
        <Grid item size={{ xs: 12, md: 6 }}>
          <Card sx={{ borderRadius: 3, border: "1px solid var(--border)", height: "100%" }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={700} sx={{ mb: 3 }}>
                Current Invoice
              </Typography>

              {currentInvoice ? (
                <Box>
                  <Box sx={{ bgcolor: "var(--bg-light)", p: 3, borderRadius: 2, mb: 3 }}>
                    <Stack direction="row" justifyContent="space-between" sx={{ mb: 2 }}>
                      <Typography variant="body2" color="var(--text-light)">Invoice #</Typography>
                      <Typography variant="body2" fontWeight={700} color="var(--primary)">
                        {currentInvoice.invoiceNumber}
                      </Typography>
                    </Stack>
                    <Divider sx={{ my: 2 }} />
                    <Stack direction="row" justifyContent="space-between" sx={{ mb: 2 }}>
                      <Typography variant="body2" color="var(--text-light)">Amount</Typography>
                      <Typography variant="h5" fontWeight={800} color="var(--primary)">
                        ₹{currentInvoice.amount?.toLocaleString()}
                      </Typography>
                    </Stack>
                    <Divider sx={{ my: 2 }} />
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="var(--text-light)">Due Date</Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {new Date(currentInvoice.dueDate).toLocaleDateString("en-IN")}
                      </Typography>
                    </Stack>
                    <Divider sx={{ my: 2 }} />
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="var(--text-light)">Status</Typography>
                      <StatusChip status={currentInvoice.status} />
                    </Stack>
                  </Box>

                  <Button
                    variant="outlined"
                    startIcon={<Download />}
                    fullWidth
                    onClick={() => handleDownloadInvoice(currentInvoice)}
                    sx={{
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 600,
                      borderColor: "var(--border)",
                      color: "var(--text)",
                      "&:hover": { borderColor: "var(--primary)", color: "var(--primary)" },
                    }}
                  >
                    Download Invoice
                  </Button>
                </Box>
              ) : (
                <Box sx={{ textAlign: "center", py: 6, bgcolor: "var(--bg-light)", borderRadius: 2 }}>
                  <CheckCircleOutline sx={{ fontSize: 64, color: "var(--primary)", mb: 2 }} />
                  <Typography variant="h6" fontWeight={600}>All Clear!</Typography>
                  <Typography variant="body2" color="var(--text-light)">No pending invoices</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Payment Section */}
        <Grid item size={{ xs: 12 }}>
          <Card sx={{ borderRadius: 3, border: "1px solid var(--border)" }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={700} sx={{ mb: 3 }}>
                Make Payment
              </Typography>

              <Grid container spacing={4}>
                {/* QR Code */}
                <Grid item size={{ xs: 12, md: 5 }}>
                  <Typography variant="body2" color="var(--text-light)" sx={{ mb: 2 }}>
                    Scan QR code or use UPI ID to pay
                  </Typography>

                  <Box
                    ref={qrRef}
                    sx={{
                      p: 3,
                      bgcolor: "white",
                      borderRadius: 2,
                      display: "inline-block",
                      border: "1px solid var(--border)",
                      mb: 2,
                    }}
                  >
                    {setting?.upiId ? (
                      <QRCode
                        value={generateUPILink() || setting.upiId}
                        size={180}
                        level="H"
                        bgColor="#ffffff"
                        fgColor={theme.palette.primary.main}
                      />
                    ) : (
                      <Box sx={{ width: 180, height: 180, display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <Typography color="var(--text-light)">No QR Code</Typography>
                      </Box>
                    )}
                  </Box>

                  <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<GetApp />}
                      onClick={handleDownloadQR}
                      sx={{ borderRadius: 2, textTransform: "none" }}
                    >
                      Download QR
                    </Button>
                  </Stack>

                  <Box sx={{ display: "flex", alignItems: "center", gap: 2, bgcolor: "var(--bg-light)", p: 2, borderRadius: 2 }}>
                    <Typography variant="body2" color="var(--text-light)">UPI ID:</Typography>
                    <Typography variant="body2" fontWeight={600}>{setting?.upiId || "Not set"}</Typography>
                    {setting?.upiId && (
                      <IconButton size="small" onClick={handleCopyUPI} sx={{ color: "var(--primary)" }}>
                        {copied ? <CheckCircleOutline fontSize="small" /> : <ContentCopy fontSize="small" />}
                      </IconButton>
                    )}
                  </Box>

                  {currentInvoice && (
                    <Typography variant="body2" fontWeight={600} sx={{ mt: 2 }}>
                      Amount: ₹{currentInvoice.amount?.toLocaleString()}
                    </Typography>
                  )}
                </Grid>

                {/* Payment Form */}
                <Grid item size={{ xs: 12, md: 7 }}>
                  <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
                    Pay to the UPI ID above and enter your UTR number below
                  </Alert>

                  <TextField
                    fullWidth
                    label="UTR Number"
                    placeholder="Enter 12-digit UTR number"
                    value={utrNumber}
                    onChange={(e) => setUtrNumber(e.target.value.toUpperCase())}
                    disabled={!currentInvoice}
                    sx={{ mb: 2 }}
                  />

                  <Button
                    variant="contained"
                    fullWidth
                    disabled={!currentInvoice || !utrNumber.trim()}
                    onClick={handleSubmitPayment}
                    size="large"
                    sx={{
                      borderRadius: 2,
                      py: 1.5,
                      textTransform: "none",
                      fontWeight: 700,
                      bgcolor: "var(--primary)",
                      "&:hover": { bgcolor: "var(--primary-dark)" },
                      "&:disabled": { bgcolor: "var(--border)" },
                    }}
                  >
                    {!currentInvoice ? "No Pending Invoice" : "Submit Payment"}
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Invoice History */}
        <Grid item size={{ xs: 12 }}>
          <Card sx={{ borderRadius: 3, border: "1px solid var(--border)" }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
                Invoice History
              </Typography>

              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: "var(--bg-light)" }}>
                      <TableCell sx={{ fontWeight: 700 }}>Invoice #</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Amount</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Due Date</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700 }}>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {invoices?.length > 0 ? (
                      invoices.slice(0, 5).map((invoice) => (
                        <TableRow key={invoice._id} hover>
                          <TableCell>#{invoice.invoiceNumber}</TableCell>
                          <TableCell>₹{invoice.amount?.toLocaleString()}</TableCell>
                          <TableCell><StatusChip status={invoice.status} /></TableCell>
                          <TableCell>{new Date(invoice.dueDate).toLocaleDateString("en-IN")}</TableCell>
                          <TableCell align="center">
                            <Tooltip title="Download PDF">
                              <IconButton
                                size="small"
                                onClick={() => handleDownloadInvoice(invoice)}
                                sx={{ color: "var(--primary)" }}
                              >
                                <PictureAsPdf fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Print">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  window.print();
                                }}
                                sx={{ color: "var(--text-light)" }}
                              >
                                <Print fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                          <Typography color="var(--text-light)">No invoices found</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* QR Dialog */}
      <Dialog open={qrDialogOpen} onClose={() => setQrDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Scan & Pay</DialogTitle>
        <DialogContent>
          <Box sx={{ textAlign: "center", py: 2 }}>
            <Box sx={{ p: 3, bgcolor: "white", borderRadius: 2, display: "inline-block", border: "1px solid var(--border)" }}>
              {setting?.upiId ? (
                <QRCode
                  value={generateUPILink() || setting.upiId}
                  size={200}
                  level="H"
                  bgColor="#ffffff"
                  fgColor={theme.palette.primary.main}
                />
              ) : (
                <Box sx={{ width: 200, height: 200, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Typography color="var(--text-light)">No QR Code</Typography>
                </Box>
              )}
            </Box>
            <Typography variant="body2" color="var(--text-light)" sx={{ mt: 2 }}>
              UPI ID: {setting?.upiId || "Not set"}
            </Typography>
            {currentInvoice && (
              <Typography variant="h6" fontWeight={700} sx={{ mt: 1 }}>
                Amount: ₹{currentInvoice.amount?.toLocaleString()}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQrDialogOpen(false)}>Close</Button>
          {setting?.upiId && (
            <Button
              variant="contained"
              onClick={() => {
                const upiLink = generateUPILink();
                if (upiLink) window.location.href = upiLink;
                setQrDialogOpen(false);
              }}
              sx={{ bgcolor: "var(--primary)", "&:hover": { bgcolor: "var(--primary-dark)" } }}
            >
              Pay Now
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Billing;